// src/components/modules/CourseMasteryCertificate.jsx
import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Award,
  Download,
  X,
  CheckCircle,
  Sparkles,
  Trophy,
  Star,
  ArrowRight,
  TrendingUp,
  Target,
  Zap
} from 'lucide-react';
import html2canvas from 'html2canvas';
import Button from '../shared/Button';
import useDialogA11y from '../../hooks/useDialogA11y';
import { useAuth } from '../../context/AuthContext';

// The same name the module certificates use: the signed-in account's name,
// otherwise the name a guest typed on a module certificate.
const GUEST_NAME_KEY = 'profoundPhilosophy_certificateName';

const CourseMasteryCertificate = ({ show, onClose, userName, completedModules = [], totalPoints = 0 }) => {
  const certificateRef = useRef(null);
  const { user } = useAuth();
  let guestName = '';
  try { guestName = localStorage.getItem(GUEST_NAME_KEY) || ''; } catch { /* storage blocked */ }
  const displayedName = userName || user?.displayName || guestName || 'Learner';
  const dialogRef = useDialogA11y(show, onClose);
  const completionDate = new Date().toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  const handleDownload = async () => {
    if (!certificateRef.current) return;

    try {
      const canvas = await html2canvas(certificateRef.current, {
        backgroundColor: '#0f172a',
        scale: 2
      });

      const link = document.createElement('a');
      link.download = `Profound-Philosophy-Mastery-Certificate.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (error) {
      console.error('Error generating certificate:', error);
    }
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-lg overflow-y-auto"
        onClick={onClose}
      >
        <div className="min-h-full flex items-center justify-center p-4 py-24 relative overflow-hidden">
          {/* Massive confetti celebration */}
          {[...Array(80)].map((_, i) => (
            <motion.div
              key={i}
              initial={{
                x: '50vw',
                y: '-10vh',
                opacity: 1,
                scale: 1.5,
                rotate: 0
              }}
              animate={{
                x: `${Math.random() * 100}vw`,
                y: `${100 + Math.random() * 20}vh`,
                opacity: 0,
                scale: 0,
                rotate: Math.random() * 1080
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                ease: 'easeOut',
                delay: i * 0.03
              }}
              className="absolute w-3 h-3 rounded-full pointer-events-none"
              style={{
                backgroundColor: ['#FFD700', '#14b8a6', '#a855f7', '#f59e0b', '#ec4899', '#3b82f6'][i % 6]
              }}
            />
          ))}

          {/* Sparkle bursts */}
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={`sparkle-${i}`}
              initial={{ scale: 0, opacity: 1 }}
              animate={{
                scale: [0, 2, 0],
                opacity: [1, 1, 0],
                rotate: Math.random() * 360
              }}
              transition={{
                duration: 2,
                delay: i * 0.1,
                repeat: Infinity,
                repeatDelay: 3
              }}
              className="absolute pointer-events-none"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
            >
              <Sparkles className="w-6 h-6 text-bright-gold" />
            </motion.div>
          ))}

          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label="Course mastery certificate"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: 'spring', duration: 0.6 }}
            className="relative max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              aria-label="Close certificate"
              className="absolute -top-4 -right-4 z-10 w-12 h-12 bg-slate-800 hover:bg-slate-700 rounded-full flex items-center justify-center transition-colors shadow-xl border-2 border-slate-600"
            >
              <X className="w-6 h-6 text-slate-300" />
            </button>

            {/* Certificate card */}
            <div
              ref={certificateRef}
              className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl shadow-2xl border-2 border-bright-gold overflow-hidden"
            >
              {/* Golden banner */}
              <div className="relative h-32 bg-gradient-to-r from-bright-gold via-yellow-400 to-bright-gold overflow-hidden">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                  animate={{ x: ['-100%', '200%'] }}
                  transition={{ duration: 3, repeat: Infinity, repeatDelay: 1 }}
                />
                <div className="relative h-full flex items-center justify-center">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                  >
                    <Trophy className="w-20 h-20 text-slate-900" />
                  </motion.div>
                </div>
              </div>

              {/* Main content */}
              <div className="p-12 text-center">
                {/* Title */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <h2 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-bright-gold via-yellow-300 to-bright-gold mb-2">
                    Course Mastery Achieved
                  </h2>
                  <p className="text-2xl text-purple-300 font-semibold mb-8">
                    Profound Philosophy: Complete
                  </p>
                </motion.div>

                {/* User name */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="mb-8"
                >
                  <p className="text-slate-400 text-lg mb-2">This certifies that</p>
                  <p className="text-4xl font-bold text-white mb-2">{displayedName}</p>
                  <p className="text-slate-400 text-lg">has completed all eight modules</p>
                </motion.div>

                {/* Module badges */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="mb-8"
                >
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                    {[
                      { num: 1, name: 'What is Philosophy', color: 'from-blue-500 to-cyan-500' },
                      { num: 2, name: 'Logic & Reasoning', color: 'from-purple-500 to-pink-500' },
                      { num: 3, name: 'Epistemology', color: 'from-green-500 to-emerald-500' },
                      { num: 4, name: 'Ethics', color: 'from-violet-500 to-purple-500' },
                      { num: 5, name: 'Metaphysics', color: 'from-orange-500 to-red-500' },
                      { num: 6, name: 'Philosophy of Mind', color: 'from-cyan-500 to-blue-500' },
                      { num: 7, name: 'Politics & Justice', color: 'from-amber-500 to-yellow-500' },
                      { num: 8, name: 'Existentialism', color: 'from-indigo-500 to-purple-500' }
                    ].map((module, idx) => (
                      <motion.div
                        key={module.num}
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ delay: 0.5 + idx * 0.1, type: 'spring' }}
                        className={`bg-gradient-to-br ${module.color} p-3 rounded-lg shadow-lg border-2 border-white/20`}
                      >
                        <CheckCircle className="w-6 h-6 text-white mx-auto mb-1" />
                        <div className="text-white font-bold text-sm">Module {module.num}</div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                {/* Stats */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 1.2 }}
                  className="grid grid-cols-3 gap-6 max-w-xl mx-auto mb-8 p-6 bg-slate-800/50 rounded-xl border border-slate-700"
                >
                  <div>
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Trophy className="w-5 h-5 text-bright-gold" />
                      <span className="text-2xl font-bold text-bright-gold">{completedModules.length}</span>
                    </div>
                    <div className="text-sm text-slate-400">Modules</div>
                  </div>
                  <div>
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Star className="w-5 h-5 text-purple-400" />
                      <span className="text-2xl font-bold text-purple-400">{totalPoints}</span>
                    </div>
                    <div className="text-sm text-slate-400">Points</div>
                  </div>
                  <div>
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <TrendingUp className="w-5 h-5 text-green-400" />
                      <span className="text-2xl font-bold text-green-400">100%</span>
                    </div>
                    <div className="text-sm text-slate-400">Complete</div>
                  </div>
                </motion.div>

                {/* Date */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 1.3 }}
                  className="mb-8"
                >
                  <p className="text-slate-400">Completed on</p>
                  <p className="text-xl text-slate-300 font-semibold">{completionDate}</p>
                </motion.div>

                {/* Congratulatory message */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 1.4 }}
                  className="mb-8 p-6 bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 border-l-4 border-indigo-500 rounded-r-xl"
                >
                  <p className="text-lg text-slate-200 leading-relaxed">
                    You've completed a journey through 2,500 years of humanity's deepest thinking, from
                    Socrates' questions to Camus' revolt. You now carry the frameworks to reason clearly,
                    weigh moral dilemmas, and examine what you believe. Your next challenge: live it.
                  </p>
                </motion.div>

                {/* Next steps */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 1.5 }}
                  className="mb-8 text-left max-w-2xl mx-auto"
                >
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <ArrowRight className="w-6 h-6 text-conversation-teal" />
                    What's Next?
                  </h3>
                  <div className="space-y-3">
                    {[
                      'Keep the toolkit in weekly rotation: thinking skills fade without practice',
                      'Revisit modules when life serves up a real dilemma',
                      'Share your knowledge: explaining an argument deepens your grasp of it',
                      'Stay curious: philosophy is a lifelong conversation'
                    ].map((step, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <Zap className="w-5 h-5 text-bright-gold mt-0.5 flex-shrink-0" />
                        <p className="text-slate-300">{step}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Action buttons */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 1.6 }}
                  className="flex flex-col sm:flex-row gap-4 justify-center"
                >
                  <Button
                    onClick={handleDownload}
                    className="bg-gradient-to-r from-conversation-teal to-cyan-500 hover:from-conversation-teal/90 hover:to-cyan-500/90 text-white font-semibold px-8 py-3 shadow-lg"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Download Certificate
                  </Button>
                  <Button
                    onClick={onClose}
                    variant="outline"
                    className="border-2 border-bright-gold text-bright-gold hover:bg-bright-gold hover:text-slate-900 font-semibold px-8 py-3"
                  >
                    Continue Learning
                  </Button>
                </motion.div>
              </div>

              {/* Bottom golden accent */}
              <div className="h-4 bg-gradient-to-r from-bright-gold via-yellow-400 to-bright-gold" />
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CourseMasteryCertificate;
